import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bot, Loader2, Wand2, ShieldQuestion, CheckCircle2, AlertTriangle, XCircle, Activity, ShieldCheck, Plus, Rocket, Archive, Trash2, Pencil, X, Copy, FlaskConical, BarChart3, ArrowDown } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import ProcessHelp from '@/components/ProcessHelp';
import KnowledgePackagePanel from '@/components/KnowledgePackagePanel';
import { Package } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

const RISK_CLS = {
  LOW: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
  MEDIUM: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
  HIGH: 'bg-red-500/15 text-red-400 border-red-500/40',
};
const STATUS_ICON = { completed: CheckCircle2, active: Loader2, paused: AlertTriangle, cancelled: XCircle, stopped_incompatible: XCircle };

// Phase 5 — Coach Autonomy control center: capability graph browser, autonomous
// opt-in toggle, a plan simulator/inspector, and recent plan-run review.
export default function AdminCoachAutonomyPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission ? hasPermission('policies:write') : false;
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [q, setQ] = useState('report of written premium by state as a bar chart');
  const [sim, setSim] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [studio, setStudio] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const EMPTY_FORM = { name: '', description: '', targetId: '', eventType: '', permissions: [], risk: 'LOW', requires: [], triggerPhrases: '', allowedModes: ['GUIDE'] };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState([]);
  const [savingCap, setSavingCap] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [phraseQ, setPhraseQ] = useState('');
  const [phraseResult, setPhraseResult] = useState(null);
  const [phraseBusy, setPhraseBusy] = useState(false);
  const [savingStyle, setSavingStyle] = useState(false);
  const [targetQuery, setTargetQuery] = useState('');
  const [discovered, setDiscovered] = useState({ candidates: [], added: [] });
  const [coverage, setCoverage] = useState(null);

  const load = async () => {
    try {
      const s = await api.getAutonomyStatus();
      setStatus(s);
      try { const r = await api.getAutonomySessions(); setSessions(r.sessions || []); } catch (e) { /* flag off → ignore */ }
      try { setMetrics(await api.getAutonomyMetrics()); } catch (e) { /* flag off → ignore */ }
      try { setTrends(await api.getAutonomyTrends()); } catch (e) { /* flag off → ignore */ }
      try { const cs = await api.listCapabilityStudio(); setStudio(cs.capabilities || []); } catch (e) { /* not admin → ignore */ }
      try { setCatalog(await api.getCapabilityStudioCatalog()); } catch (e) { /* not admin → ignore */ }
      try { setDiscovered(await api.listDiscoveredTargets()); } catch (e) { /* not admin → ignore */ }
      try { setCoverage(await api.getCoachCoverage()); } catch (e) { /* not admin → ignore */ }
    } catch (err) { toast.error(err.message || 'Could not load autonomy settings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (val) => {
    try {
      await api.setAutonomyEnabled(val);
      setStatus((p) => ({ ...p, enabled: val }));
      toast[val ? 'success' : 'message'](`Autonomous Coach ${val ? 'enabled' : 'disabled'} for this workspace`);
      if (val) load();
    } catch (e) { toast.error(e.message); }
  };

  const simulate = async () => {
    if (!q.trim()) return;
    setSimBusy(true); setSim(null);
    try { setSim(await api.simulateAutonomousPlan({ question: q.trim() })); }
    catch (e) { toast.error(e.message || 'Simulation failed (is autonomy enabled?)'); }
    finally { setSimBusy(false); }
  };

  // ── Capability Studio ──
  const reloadStudio = async () => { try { const cs = await api.listCapabilityStudio(); setStudio(cs.capabilities || []); } catch (e) { /* ignore */ } await load(); };
  const addDiscovered = async (c) => {
    try {
      await api.addDiscoveredTarget({ targetId: c.target_id, label: c.label });
      toast.success(`Added "${c.label}" to the target catalog`);
      const [cat, disc] = await Promise.all([api.getCapabilityStudioCatalog(), api.listDiscoveredTargets()]);
      setCatalog(cat); setDiscovered(disc);
    } catch (e) { toast.error(e.message || 'Could not add target'); }
  };
  const toggleIn = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const openNewForm = () => { setEditingId(null); setForm(EMPTY_FORM); setFormErrors([]); setPhraseQ(''); setPhraseResult(null); setShowForm(true); };
  const openEditForm = (c) => {
    setEditingId(c.studio_id);
    setForm({ name: c.name || '', description: c.description || '', targetId: c.targetId || '', eventType: c.eventType || '',
      permissions: c.permissions || [], risk: c.risk || 'LOW', requires: c.requires || [],
      triggerPhrases: (c.trigger_phrases || []).join(', '), allowedModes: c.allowedModes || ['GUIDE'] });
    setFormErrors([]); setPhraseQ(''); setPhraseResult(null); setShowForm(true);
  };
  const openCloneForm = (c) => {
    setEditingId(null);
    setForm({ name: `${c.name || 'Capability'} (copy)`, description: c.description || '', targetId: c.targetId || '', eventType: c.eventType || '',
      permissions: c.permissions || [], risk: c.risk || 'LOW', requires: c.requires || [],
      triggerPhrases: '', allowedModes: c.allowedModes || ['GUIDE'] });
    setFormErrors([]); setPhraseQ(''); setPhraseResult(null); setShowForm(true);
    toast.message('Cloned into a new draft — give it a distinct name and trigger phrases');
  };
  const testPhrase = async () => {
    if (!phraseQ.trim()) return;
    setPhraseBusy(true); setPhraseResult(null);
    try {
      const r = await api.testCapabilityStudioPhrase({
        question: phraseQ.trim(),
        triggerPhrases: form.triggerPhrases.split(',').map((s) => s.trim()).filter(Boolean),
        name: form.name || 'this capability',
      });
      setPhraseResult(r);
    } catch (e) { toast.error(e.message || 'Could not test the phrase'); }
    finally { setPhraseBusy(false); }
  };
  const applySuggestion = (s) => {
    setForm((f) => ({ ...f, triggerPhrases: f.triggerPhrases.trim() ? `${f.triggerPhrases}, ${s}` : s }));
    setPhraseQ(s); setPhraseResult(null);
    toast.message('Added the suggested phrase — test it again to confirm');
  };
  const saveStyle = async (next) => {
    setSavingStyle(true);
    try {
      const r = await api.setAutonomyHighlightStyle(next);
      setStatus((s) => ({ ...s, highlight_style: r.highlight_style }));
      toast.success('Highlight arrow updated');
    } catch (e) { toast.error(e.message || 'Could not update highlight'); }
    finally { setSavingStyle(false); }
  };
  const submitCap = async () => {
    setSavingCap(true); setFormErrors([]);
    const body = { name: form.name, description: form.description, targetId: form.targetId, eventType: form.eventType,
      permissions: form.permissions, risk: form.risk, requires: form.requires, allowedModes: form.allowedModes,
      triggerPhrases: form.triggerPhrases.split(',').map((s) => s.trim()).filter(Boolean) };
    try {
      const res = editingId ? await api.updateCapabilityStudio(editingId, body) : await api.createCapabilityStudio(body);
      if (res && res.ok === false) { setFormErrors(res.errors || [{ code: 'invalid' }]); }
      else { toast.success(editingId ? 'Capability updated' : 'Draft capability created'); setShowForm(false); setForm(EMPTY_FORM); setEditingId(null); await reloadStudio(); }
    } catch (e) { toast.error(e.message || 'Could not save capability'); }
    finally { setSavingCap(false); }
  };
  const publishCap = async (c) => {
    try { const r = await api.publishCapabilityStudio(c.studio_id);
      if (r && r.ok === false) { toast.error(`Cannot publish: ${(r.errors || [])[0]?.code || 'invalid'}`); }
      else { toast.success(`Published "${c.name}" — now a live coachable task`); await reloadStudio(); }
    } catch (e) { toast.error(e.message); }
  };
  const retireCap = async (c) => { try { await api.retireCapabilityStudio(c.studio_id); toast.message(`Retired "${c.name}"`); await reloadStudio(); } catch (e) { toast.error(e.message); } };
  const deleteCap = async (c) => { try { await api.deleteCapabilityStudio(c.studio_id); toast.message('Draft deleted'); await reloadStudio(); } catch (e) { toast.error(e.message); } };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="autonomy-loading"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="admin-coach-autonomy">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center"><Bot className="w-6 h-6 text-primary" /></div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Coach Autonomy</h1>
            <p className="text-sm text-muted-foreground">Capability graph, the autonomous opt-in, a plan inspector, and recent plan runs. Completion stays server-decided.</p>
          </div>
        </div>
        <ProcessHelp
          testId="autonomy-help"
          title="Coach Autonomy"
          intro="Control the Autonomous AI Coach: turn it on, browse what it can guide, preview plans, and watch how runs trend."
          steps={[
            'Toggle “Autonomous plan runtime” on to let users generate a validated plan and step through it with live, server-verified completion.',
            'Browse the capability graph to see every coachable task and the permissions and screen targets it needs.',
            'Use the Plan Inspector to preview the exact steps for any request — read-only, no runtime required.',
            'Review recent plan runs and the Plan run trends chart to watch completion vs deviation over time.',
          ]}
        />
      </div>

      {/* Opt-in */}
      <Card className="p-5 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Autonomous plan runtime</p>
          <p className="text-xs text-muted-foreground">Off by default. When on, users can generate a validated plan and step through it with live server verification.</p>
        </div>
        <Switch checked={!!status?.enabled} onCheckedChange={toggle} disabled={!canManage} aria-label="Toggle autonomous Coach" data-testid="autonomy-toggle" />
      </Card>

      {/* Coach highlight arrow style */}
      <Card className="p-5 space-y-3" data-testid="highlight-style-card">
        <div>
          <p className="text-sm font-semibold text-foreground">Highlight arrow</p>
          <p className="text-xs text-muted-foreground">The bouncing arrow that points at the button the Coach is guiding to. Pick a color, or turn the bounce off for a calmer look.</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2" data-testid="arrow-color-swatches">
            {Object.entries(status?.highlight_colors || { red: '#ef4444', amber: '#f59e0b', blue: '#3b82f6', green: '#22c55e', purple: '#a855f7', pink: '#ec4899' }).map(([nm, hex]) => {
              const active = (status?.highlight_style?.color || '#ef4444') === hex;
              return (
                <button key={nm} type="button" title={nm} disabled={!canManage || savingStyle}
                  onClick={() => saveStyle({ color: hex, bounce: status?.highlight_style?.bounce ?? true })}
                  data-testid={`arrow-color-${nm}`}
                  className={`w-7 h-7 rounded-full border-2 transition ${active ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'} disabled:opacity-50`}
                  style={{ backgroundColor: hex }} />
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Bounce</span>
            <Switch checked={status?.highlight_style?.bounce ?? true} disabled={!canManage || savingStyle}
              onCheckedChange={(v) => saveStyle({ color: status?.highlight_style?.color || '#ef4444', bounce: v })}
              aria-label="Toggle arrow bounce" data-testid="arrow-bounce-toggle" />
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="arrow-preview">
            preview
            <ArrowDown className={`w-4 h-4${(status?.highlight_style?.bounce ?? true) ? ' animate-bounce' : ''}`} strokeWidth={3} style={{ color: status?.highlight_style?.color || '#ef4444' }} />
          </div>
        </div>
      </Card>

      {/* Capability Studio — add / retire coachable tasks without code changes */}
      <Card className="p-5 space-y-3" data-testid="capability-studio">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground flex items-center gap-2"><Wand2 className="w-4 h-4 text-primary" /> Capability Studio</p>
            <p className="text-xs text-muted-foreground">Compose new coachable tasks from existing screen targets, trusted events and permissions. Published tasks become real, governed capabilities — no code changes.</p>
          </div>
          {canManage && !showForm && (
            <Button size="sm" onClick={openNewForm} className="gap-1.5 shrink-0" data-testid="studio-new">
              <Plus className="w-3.5 h-3.5" /> New capability
            </Button>
          )}
        </div>

        {(() => {
          const topUsed = [...studio].filter((c) => c.status === 'published' && (c.runs || 0) > 0).sort((a, b) => (b.runs || 0) - (a.runs || 0)).slice(0, 3);
          if (topUsed.length === 0) return null;
          return (
            <div className="rounded-md border border-border/40 bg-muted/20 p-2.5" data-testid="studio-most-used">
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-1"><BarChart3 className="w-3 h-3 text-primary" /> Most-run capabilities</p>
              <div className="flex flex-wrap gap-2">
                {topUsed.map((c, i) => (
                  <span key={c.studio_id} className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-foreground border border-primary/30">
                    {i === 0 && '🏆 '}{c.name} · <strong>{c.runs}</strong> {c.runs === 1 ? 'run' : 'runs'}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {(discovered.candidates || []).filter((c) => c.status !== 'added').length > 0 && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5" data-testid="studio-discovered">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 mb-1"><FlaskConical className="w-3 h-3 text-amber-400" /> Discovered from recordings — controls people used that aren't in the catalog yet</p>
            <div className="space-y-1">
              {(discovered.candidates || []).filter((c) => c.status !== 'added').slice(0, 8).map((c) => (
                <div key={c.target_id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="min-w-0 truncate">{c.label} <code className="opacity-50">{c.target_id}</code> <span className="text-muted-foreground">· {c.count}×</span></span>
                  {canManage && <button onClick={() => addDiscovered(c)} className="shrink-0 text-[11px] px-1.5 py-0.5 rounded border border-primary/40 text-primary hover:bg-primary/10" data-testid={`studio-discovered-add-${c.target_id}`}>Add</button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {showForm && catalog && (
          <div className="rounded-lg border border-border/50 p-4 space-y-3" data-testid="studio-form">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">{editingId ? 'Edit draft capability' : 'New capability'}</span>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-muted-foreground hover:text-foreground" aria-label="Close form" data-testid="studio-form-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Name</label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Open the schedule panel" className="text-sm" data-testid="studio-name" />
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Risk</label>
                <select value={form.risk} onChange={(e) => setForm((f) => ({ ...f, risk: e.target.value }))} className="w-full text-sm h-9 px-2 rounded-md border border-border/60 bg-background" data-testid="studio-risk">
                  {(catalog.risks || ['LOW', 'MEDIUM']).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Description (what the Coach guides the user to do)</label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Short, action-oriented instruction" className="text-sm" data-testid="studio-description" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-muted-foreground">Screen target (highlighted element)</label>
                <div data-testid="studio-target-picker">
                  <Input value={targetQuery} onChange={(e) => setTargetQuery(e.target.value)} placeholder="Search screens & buttons…" className="text-sm mb-1" data-testid="studio-target-search" />
                  {form.targetId && <p className="text-[10px] text-primary mb-1" data-testid="studio-target-selected">Selected: <code>{form.targetId}</code></p>}
                  <div className="max-h-44 overflow-y-auto rounded-md border border-border/50 divide-y divide-border/30" data-testid="studio-target">
                    {(() => {
                      const q = targetQuery.trim().toLowerCase();
                      const items = (catalog.targetCatalog || []).filter((t) => !q || t.label.toLowerCase().includes(q) || t.id.toLowerCase().includes(q) || (t.area || '').toLowerCase().includes(q));
                      const groups = {};
                      items.forEach((t) => { (groups[t.area || 'Other'] = groups[t.area || 'Other'] || []).push(t); });
                      const areas = Object.keys(groups).sort();
                      if (areas.length === 0) return <p className="text-[11px] text-muted-foreground p-2">No matching controls.</p>;
                      return areas.map((area) => (
                        <div key={area}>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30 px-2 py-1 sticky top-0">{area}</div>
                          {groups[area].map((t) => (
                            <button key={t.id} type="button" onClick={() => setForm((f) => ({ ...f, targetId: t.id }))}
                              className={`w-full text-left px-2 py-1.5 text-[12px] flex items-center justify-between gap-2 ${form.targetId === t.id ? 'bg-primary/15 text-primary' : 'hover:bg-muted/40'}`}
                              data-testid={`studio-target-opt-${t.id}`}>
                              <span>{t.label}</span>
                              <code className="text-[9px] opacity-50 shrink-0">{t.id}</code>
                            </button>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[11px] text-muted-foreground">Completion event (proves the step is done)</label>
                <select value={form.eventType} onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))} className="w-full text-sm h-9 px-2 rounded-md border border-border/60 bg-background" data-testid="studio-event">
                  <option value="">— choose an event —</option>
                  {(catalog.events || []).map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Required permissions</label>
              <div className="mt-1 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-1" data-testid="studio-permissions">
                {(catalog.permissions || []).map((p) => (
                  <button key={p} type="button" onClick={() => toggleIn('permissions', p)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${form.permissions.includes(p) ? 'bg-primary/15 text-primary border-primary/40' : 'border-border/60 text-muted-foreground hover:bg-muted/40'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Prerequisite steps (optional — run before this one)</label>
              <div className="mt-1 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto p-1" data-testid="studio-requires">
                {(catalog.prerequisites || []).map((c) => (
                  <button key={c.capabilityId} type="button" onClick={() => toggleIn('requires', c.capabilityId)}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${form.requires.includes(c.capabilityId) ? 'bg-primary/15 text-primary border-primary/40' : 'border-border/60 text-muted-foreground hover:bg-muted/40'}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Trigger phrases (comma-separated — how users ask for this in natural language)</label>
              <Input value={form.triggerPhrases} onChange={(e) => setForm((f) => ({ ...f, triggerPhrases: e.target.value }))} placeholder="e.g. run the compliance sweep, start compliance check" className="text-sm" data-testid="studio-triggers" />
            </div>
            {/* NL Trigger Tester — check what a phrase resolves to BEFORE publishing */}
            <div className="rounded-md border border-border/40 bg-muted/20 p-2.5 space-y-2" data-testid="studio-phrase-tester">
              <label className="text-[11px] text-muted-foreground flex items-center gap-1.5"><FlaskConical className="w-3 h-3 text-primary" /> Test a phrase — see which capability it would trigger</label>
              <div className="flex items-center gap-2">
                <Input value={phraseQ} onChange={(e) => setPhraseQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); testPhrase(); } }} placeholder="Type a sample question a user might ask…" className="text-sm" data-testid="studio-phrase-input" />
                <Button size="sm" variant="outline" onClick={testPhrase} disabled={phraseBusy} className="gap-1.5 shrink-0" data-testid="studio-phrase-test">
                  {phraseBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />} Test
                </Button>
              </div>
              {phraseResult && (
                <p className="text-[11px]" data-testid="studio-phrase-result">
                  {phraseResult.isDraft ? (
                    <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> This phrase would trigger <strong>this capability</strong> — good to publish.</span>
                  ) : phraseResult.isStudio ? (
                    <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Would trigger another custom capability (<code>{phraseResult.matchedCapabilityId}</code>) — pick a more distinctive phrase.</span>
                  ) : phraseResult.builtin ? (
                    <span className="text-amber-400 flex items-center gap-1.5 flex-wrap"><AlertTriangle className="w-3 h-3" /> Would trigger the built-in “{phraseResult.desiredOutcome || phraseResult.goalType}” instead — built-ins always win, so choose a more distinctive phrase.
                      {phraseResult.suggestion && (
                        <button type="button" onClick={() => applySuggestion(phraseResult.suggestion)} className="ml-1 text-[11px] px-1.5 py-0.5 rounded border border-primary/40 text-primary hover:bg-primary/10" data-testid="studio-phrase-suggestion">Try “{phraseResult.suggestion}”</button>
                      )}
                    </span>
                  ) : (
                    <span className="text-muted-foreground flex items-center gap-1"><ShieldQuestion className="w-3 h-3" /> Nothing matches this yet — add a trigger phrase above that appears in the question.</span>
                  )}
                </p>
              )}
            </div>
            {formErrors.length > 0 && (
              <div className="text-[11px] text-red-400 space-y-0.5" data-testid="studio-errors">
                {formErrors.map((er, i) => <div key={i} className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {er.field ? `${er.field}: ` : ''}{er.code}{er.value ? ` (${er.value})` : ''}{er.detail ? ` — ${er.detail}` : ''}</div>)}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={submitCap} disabled={savingCap} className="gap-1.5" data-testid="studio-save">
                {savingCap ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} {editingId ? 'Save draft' : 'Create draft'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
            </div>
          </div>
        )}

        {studio.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="no-studio-caps">No custom capabilities yet. Create one to add a coachable task without code.</p>
        ) : (
          <div className="space-y-2" data-testid="studio-list">
            {studio.map((c) => {
              const badge = c.status === 'published' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40'
                : c.status === 'retired' ? 'bg-muted text-muted-foreground border-border/60'
                : 'bg-amber-500/15 text-amber-400 border-amber-500/40';
              return (
                <div key={c.studio_id} className="rounded-lg border border-border/50 p-3" data-testid={`studio-cap-${c.studio_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium truncate">{c.name}</span>
                        <Badge className={`text-[10px] border ${badge}`} data-testid={`studio-status-${c.studio_id}`}>{c.status}</Badge>
                        <Badge className={`text-[10px] border ${RISK_CLS[c.risk] || RISK_CLS.LOW}`}>{c.risk}</Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5"><code>{c.capability_id}</code></p>
                      <p className="text-[10px] text-muted-foreground mt-1">target: <code>{c.targetId}</code> · event: <code>{c.eventType}</code> · perms: {(c.permissions || []).join(', ') || 'none'}</p>
                      {(c.trigger_phrases || []).length > 0 && <p className="text-[10px] text-muted-foreground mt-0.5">say: “{(c.trigger_phrases || []).join('”, “')}”</p>}
                      {c.status === 'published' && (
                        <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1" data-testid={`studio-usage-${c.studio_id}`}>
                          <BarChart3 className="w-3 h-3 text-primary" /> run <strong>{c.runs || 0}</strong> {(c.runs || 0) === 1 ? 'time' : 'times'} · <strong>{c.completions || 0}</strong> completed
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openCloneForm(c)} title="Duplicate" className="p-1.5 rounded-md hover:bg-muted/50 text-muted-foreground" data-testid={`studio-clone-${c.studio_id}`}><Copy className="w-3.5 h-3.5" /></button>
                        {c.status === 'draft' && <button onClick={() => openEditForm(c)} title="Edit" className="p-1.5 rounded-md hover:bg-muted/50 text-muted-foreground" data-testid={`studio-edit-${c.studio_id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                        {c.status !== 'published' && <button onClick={() => publishCap(c)} title="Publish" className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400" data-testid={`studio-publish-${c.studio_id}`}><Rocket className="w-3.5 h-3.5" /></button>}
                        {c.status === 'published' && <button onClick={() => retireCap(c)} title="Retire" className="p-1.5 rounded-md hover:bg-amber-500/10 text-amber-400" data-testid={`studio-retire-${c.studio_id}`}><Archive className="w-3.5 h-3.5" /></button>}
                        {c.status !== 'published' && <button onClick={() => deleteCap(c)} title="Delete" className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" data-testid={`studio-delete-${c.studio_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Coverage — how much of the app the Coach can reach */}
      <Card className="p-5 space-y-3" data-testid="coach-coverage">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Coverage</p>
            <p className="text-xs text-muted-foreground">Which menus &amp; buttons the Coach can point at, by area — and how many already have a coachable task. Controls seen in recordings but not yet catalogued show as gaps to fill.</p>
          </div>
          <ProcessHelp
            testId="coverage-help"
            title="Coach coverage"
            intro="A read-only picture of how far the Coach reaches across the app."
            steps={[
              'Catalogued = controls the Coach can highlight and guide to today.',
              'Coached = catalogued controls that at least one built-in or published capability points at.',
              'Gaps to fill = controls people used in recordings that are not catalogued yet — promote them in Capability Studio.',
            ]}
          />
        </div>
        {coverage ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ['Areas', coverage.totals?.areas ?? 0],
                ['Catalogued', coverage.totals?.targets ?? 0],
                ['Coached', coverage.totals?.coached ?? 0],
                ['Gaps to fill', coverage.totals?.discovered_pending ?? 0],
              ].map(([label, val]) => (
                <div key={label} className="rounded-lg border border-border/50 p-3">
                  <div className="text-lg font-semibold text-foreground" data-testid={`coverage-${label.toLowerCase().replace(/\s+/g, '-')}`}>{val}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1.5" data-testid="coverage-area-list">
              {(coverage.areas || []).map((a) => {
                const pct = a.targets ? Math.round((a.coached / a.targets) * 100) : 0;
                return (
                  <div key={a.area} className="flex items-center gap-3" data-testid={`coverage-area-${a.area}`}>
                    <span className="text-[11px] text-muted-foreground w-44 shrink-0 truncate" title={a.area}>{a.area}</span>
                    <div className="flex-1 h-2 rounded-full bg-muted/40 overflow-hidden">
                      <div className="h-full bg-primary/70 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[11px] text-muted-foreground w-16 text-right shrink-0">{a.coached}/{a.targets}</span>
                  </div>
                );
              })}
            </div>
            {(coverage.discovered_pending || []).length > 0 && (
              <p className="text-[11px] text-amber-400 flex items-start gap-1" data-testid="coverage-gaps-note">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                {coverage.discovered_pending.length} control{coverage.discovered_pending.length === 1 ? '' : 's'} seen in recordings aren&apos;t catalogued yet — promote them from “Discovered from recordings” above.
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="no-coverage">Coverage appears once autonomy is enabled.</p>
        )}
      </Card>

      {/* Knowledge package — move AI Coach knowledge between servers */}
      <KnowledgePackagePanel
        testIdPrefix="coach-knowledge"
        icon={Package}
        kind="coach"
        title="AI Coach knowledge package"
        description="Export capabilities, discovered targets and published walkthroughs as a portable file, then upload it on another server to move this Coach's knowledge."
        filenameBase="ai-coach-knowledge"
        exportFn={(note) => api.exportCoachKnowledge(note)}
        importFn={(pkg, _modelId, include) => api.importCoachKnowledge(pkg, include)}
      />

      {/* Capability graph browser */}      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Registered capabilities <span className="text-xs text-muted-foreground font-normal">({(status?.capabilities || []).length})</span></p>
          <span className="text-[11px] text-muted-foreground">product {status?.productVersion}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="capability-list">
          {(status?.capabilities || []).map((c) => (
            <div key={c.capabilityId} className="rounded-lg border border-border/50 p-3" data-testid={`capability-${c.capabilityId}`}>
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium">{c.name}</span>
                <Badge className={`text-[10px] border ${RISK_CLS[c.risk] || RISK_CLS.LOW}`}>{c.risk}</Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5"><code>{c.capabilityId}</code> · {c.domain}</p>
              <p className="text-[10px] text-muted-foreground mt-1">perms: {(c.permissions || []).join(', ') || 'none'} · target: <code>{c.targetId}</code></p>
            </div>
          ))}
        </div>
      </Card>

      {/* Plan inspector */}
      <Card className="p-5 space-y-3">
        <p className="text-sm font-semibold text-foreground flex items-center gap-2"><Wand2 className="w-4 h-4 text-primary" /> Plan inspector</p>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Describe a goal to plan…" className="text-sm" data-testid="sim-input" />
          <Button size="sm" onClick={simulate} disabled={simBusy} data-testid="sim-run" className="gap-1.5 shrink-0">
            {simBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Plan
          </Button>
        </div>
        {sim && (
          <div className="rounded-lg border border-border/50 p-3 space-y-2" data-testid="sim-result">
            {sim.plan ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-[13px] font-medium">{sim.goal?.desiredOutcome || sim.goal?.goalType}</span>
                  <Badge className={`text-[10px] border ${RISK_CLS[sim.plan.risk] || RISK_CLS.LOW}`}>{sim.plan.risk}</Badge>
                </div>
                <ol className="space-y-1">
                  {(sim.plan.steps || []).map((s, i) => (
                    <li key={s.stepId} className="text-[12px] text-muted-foreground">{i + 1}. <code>{s.capabilityId}</code></li>
                  ))}
                </ol>
                <p className="text-[11px]">
                  {sim.validation?.ok
                    ? <span className="text-emerald-400">✓ validated{sim.validation.requires_confirmation ? ' · needs confirmation' : ''}</span>
                    : <span className="text-red-400 flex items-center gap-1"><ShieldQuestion className="w-3 h-3" /> rejected: {(sim.validation?.errors || [])[0]?.code || 'invalid'}</span>}
                </p>
              </>
            ) : (
              <p className="text-[12px] text-amber-400 flex items-center gap-1" data-testid="sim-blocked"><AlertTriangle className="w-3.5 h-3.5" /> {
                sim.blocked?.code === 'missing_parameters'
                  ? `Needs more detail — provide ${(sim.blocked.parameters || []).map(p => ({ measureField: 'a measure', categoryField: 'a dimension', field: 'a filter field' }[p] || p)).join(' and ') || 'more info'}`
                  : sim.blocked?.code === 'no_capability_for_goal'
                    ? 'No capability for this goal yet'
                    : `No plan — ${sim.blocked?.code || 'unsupported goal'}`
              }</p>
            )}
          </div>
        )}
      </Card>

      {/* Plan run health (Phase 7) */}
      <Card className="p-5 space-y-3" data-testid="plan-health">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> Plan run health</p>
          {metrics?.health && (
            metrics.health.alerts?.length ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 flex items-center gap-1" data-testid="health-alert"><AlertTriangle className="w-3 h-3" /> {metrics.health.alerts.join(', ')}</span>
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 flex items-center gap-1" data-testid="health-ok"><ShieldCheck className="w-3 h-3" /> Healthy</span>
            )
          )}
        </div>
        {metrics ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                ['Sessions', metrics.sessions?.total ?? 0],
                ['Completion', `${Math.round((metrics.completion_rate || 0) * 100)}%`],
                ['Deviation rate', `${Math.round((metrics.deviation?.rate || 0) * 100)}%`],
                ['Undos', metrics.undos ?? 0],
              ].map(([label, val]) => (
                <div key={label} className="rounded-lg border border-border/50 p-3">
                  <div className="text-lg font-semibold text-foreground" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>{val}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" /> False completions: <strong>{metrics.health?.false_completions ?? 0}</strong> — completion is verified server-side on every step (structural guarantee).
            </p>
          </>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="no-metrics">No metrics yet — enable autonomy and run a plan.</p>
        )}
      </Card>

      {/* Plan run trends (Phase 7) — completion vs deviation over time */}
      <Card className="p-5 space-y-3" data-testid="plan-trends">
        <p className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" /> Plan run trends
          <span className="text-[11px] font-normal text-muted-foreground">last {trends?.days ?? 30} days</span>
        </p>
        {trends && (trends.totals?.started || 0) > 0 ? (
          <div style={{ height: 220 }} data-testid="trends-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trends.points} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tickFormatter={(d) => String(d).slice(5)} tick={{ fontSize: 10, fill: '#94a3b8' }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} width={28} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="started" name="Started" stroke="#22d3ee" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="completed" name="Completed" stroke="#34d399" strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="deviated" name="Deviated" stroke="#f59e0b" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="no-trends">No plan runs in this window yet — trends appear once plans start running.</p>
        )}
      </Card>

      {/* Recent plan runs */}
      <Card className="p-5 space-y-3">
        <p className="text-sm font-semibold text-foreground">Recent plan runs <span className="text-xs text-muted-foreground font-normal">(redacted)</span></p>
        {sessions.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="no-sessions">No plan runs yet.</p>
        ) : (
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map((s) => {
              const Icon = STATUS_ICON[s.status] || Loader2;
              return (
                <div key={s.plan_session_id} className="flex items-start justify-between gap-3 rounded-lg border border-border/50 p-3" data-testid={`session-${s.plan_session_id}`}>
                  <div>
                    <div className="text-[13px] font-medium flex items-center gap-1.5"><Icon className={`w-3.5 h-3.5 ${s.status === 'completed' ? 'text-emerald-400' : s.status === 'cancelled' || s.status === 'stopped_incompatible' ? 'text-red-400' : 'text-muted-foreground'}`} /> {s.desiredOutcome || s.goalType}</div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">step {(s.current_step_index ?? 0) + 1}/{s.total_steps} · {s.deviation_count} deviations · {s.status}</p>
                  </div>
                  {s.risk && <Badge className={`text-[10px] border ${RISK_CLS[s.risk] || RISK_CLS.LOW}`}>{s.risk}</Badge>}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
